import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router";
import type { TravelGuide } from "@/shared/contracts";
import { useCurrentHousehold } from "@/shared/session/householdContext";
import { Button, Card, EmptyState, ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { PlanShelf, useLongingState, useShelfViews } from "./travelPlan/PlanPage";
import { useCurrentWish } from "./travelPlan/data";
import { hasWrittenPlan } from "./travelPlan/model";
import { readSceneEntry, safeSceneHref, SourceBack, SourceLink } from "@/shared/ui/sceneNavigation";
import { useCurrentGuides, useCurrentGuideDetail, type GuideRead } from "./guideCurrentScope";
import "./guide.css";

const STATUS_TEXT: Record<string, string> = { planned: "还在计划", in_progress: "正在路上", completed: "这段旅程已结束" };

function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("zh-CN", { month: "long", day: "numeric" });
}

function useGuides(selectedId?: string) { return useCurrentGuides(selectedId); }

function GuideReadView<T>({ read, children, isEmpty, empty }: { read: GuideRead<T>; children: (data: T) => ReactNode; isEmpty?: (data: T) => boolean; empty?: ReactNode }) {
  if (read.state === "unavailable" || read.state === "loading") return <LoadingState label="正在确认可查看的手账…" />;
  if (read.state === "error") return <ErrorState error={read.error} onRetry={read.retry} />;
  if (isEmpty?.(read.data)) return <>{empty ?? <EmptyState title="这里还空着" />}</>;
  return <>{children(read.data)}</>;
}

export function GuideBookPage() {
  const { userId, pet, household } = useCurrentHousehold();
  const location = useLocation();
  const contentIntro = useRef<HTMLDivElement>(null);
  const pendingClear = useRef<{ trigger: HTMLAnchorElement; scope: string } | null>(null);
  const [params] = useSearchParams();
  const journeyId = params.get("journey");
  const readerScope = JSON.stringify([userId, household?.household_id ?? null, household?.home_id ?? null, pet?.pet_id ?? null]);
  const returnEntry = readSceneEntry(location.state, safeSceneHref(location.pathname + location.search));
  useLayoutEffect(() => {
    const pending = pendingClear.current;
    pendingClear.current = null;
    if (!journeyId && location.pathname === "/guides" && location.search === "" && pending?.scope === readerScope
      && !pending.trigger.isConnected && document.activeElement === document.body
      && contentIntro.current?.isConnected) contentIntro.current.focus({ preventScroll: true });
  }, [journeyId, location.key, location.pathname, location.search, readerScope]);
  const { read: guides } = useGuides();
  // TRV-06：列表分两块，“想去 / 准备中”（心愿与计划卡片）在上，“去过的”（下面这份原有攻略列表）在下。
  // 没有可显示的心愿和计划时（live 没有活动心愿）useShelfViews() 为 null，这一块不显示、页面与原来一样；只看某一趟旅程（?journey=）时也不显示。
  const shelf = useShelfViews();
  const plans = journeyId ? null : shelf;
  // 惦记（UI-LONGING-WIRE-01）：TA 惦记着一处时，“想去 / 准备中”这一块即使没有心愿和计划也显示（单列一张惦记卡）；只看某一趟旅程时不显示
  // 只因惦记显示时先等心愿读完、确实没有心愿（不先说“没有准备去的地方”再改口）；有心愿时等 useShelfViews 给出心愿那张卡
  const longingState = useLongingState();
  const wishRead = useCurrentWish();
  const noWish = wishRead.state === "ready" && !wishRead.data;
  const longing = !journeyId && longingState.state === "longing" && (plans || noWish) ? longingState.view : null;
  const shelfShown = Boolean(plans || longing);
  const pastId = useId();
  // 上面有写好的计划时（计划发布时就写好了计划手账，回忆页也把它算一份），下面“去过的”空着不能说“还没有攻略手账”（DS5，DS4 报）；
  // 上面只有还没写好计划的心愿（资料还在查、待补）时，确实一份手账都没有，照原来说。
  const empty = plans?.some(hasWrittenPlan) ? <p className="ps-plan-muted">除了上面的计划，还没有别的攻略手账。</p> : <EmptyState icon="bookmark" title="还没有攻略手账" action={<Link className="ps-guide-empty-nav" to="/journey"><Icon name="compass" size={18} />看看TA的旅途<Icon name="chevron" size={16} /></Link>}>TA 准备好一趟旅程后，手账会出现在这里。</EmptyState>;
  const past = <GuideReadView read={guides} isEmpty={(list) => list.length === 0 && !journeyId} empty={empty}>
      {(list) => {
        const visible = journeyId ? list.filter((guide) => guide.journey_id === journeyId) : list;
        return visible.length === 0 ? <EmptyState icon="bookmark" title="这趟旅程还没有手账">目前没有与这趟行程关联的攻略，不代 TA 编一份。<Link to="/guides" state={returnEntry ? { sceneEntry: returnEntry } : undefined} onClick={event => {
          if (!event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey
            && !event.shiftKey && !event.altKey && document.activeElement === event.currentTarget) {
            pendingClear.current = { trigger: event.currentTarget, scope: readerScope };
          }
        }} onBlur={() => { pendingClear.current = null; }}>查看全部手账</Link></EmptyState> : <div className="ps-guide-list">{visible.map((guide) => <SourceLink key={guide.guide_id} to={`/guides/${encodeURIComponent(guide.guide_id)}`} className="ps-guide-cover"><span className="ps-guide-cover__city">{guide.city}</span><strong>{guide.title}</strong><span className="ps-guide-cover__meta">{dateLabel(guide.created_at)} · {STATUS_TEXT[guide.status ?? ""] ?? "状态待确认"}</span><span className="ps-guide-cover__open">翻开手账 <Icon name="chevron" size={15} /></span></SourceLink>)}</div>;
      }}
    </GuideReadView>;
  return <Page className="ps-guide-page">
    <TopBar title={`${pet?.name ?? "TA"} 的攻略手账`} subtitle="计划与已经发生的事，分开写" sourceBack={{ fallback: "/memories", fallbackLabel: "返回回忆" }} />
    <div ref={contentIntro} tabIndex={-1} role="region" aria-label="旅行手账内容" className="ps-guide-intro"><Icon name="bookmark" size={23} /><div><strong>翻开 TA 的旅行笔记</strong><p>TA 一路走过、想去的地方，都记在这里。</p></div></div>
    {shelfShown ? <PlanShelf views={plans ?? []} longing={longing} /> : null}
    {shelfShown ? <section className="ps-plan-shelf" aria-labelledby={pastId} data-shelf="past"><div className="ps-plan-shelf__head"><h2 id={pastId}>去过的</h2></div>{past}</section> : past}
  </Page>;
}

function GuideImage({ url }: { url: string }) {
  const [state, setState] = useState<"loading" | "loaded" | "error">("loading");
  const alive = useRef(false);
  const image = useRef<HTMLImageElement>(null);
  useLayoutEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  const isCurrent = (node: HTMLImageElement) => alive.current && image.current === node && node.isConnected;
  return <figure className="ps-guide-image" data-guide-image-state={state}>
    {state !== "error" ? <img ref={image} src={url} alt="TA 的旅行手账插画" onLoad={(event) => { if (isCurrent(event.currentTarget)) setState("loaded"); }} onError={(event) => { if (isCurrent(event.currentTarget)) setState("error"); }} /> : null}
    {state !== "loaded" ? <div className="ps-guide-image-state" role="status" aria-live="polite">{state === "error" ? "插画暂时没读到，文字照常可读" : "手账插画加载中…"}</div> : null}
    <figcaption>路线与地址以文字里核对过的为准。</figcaption>
  </figure>;
}

function GuideCopy({ text }: { text: string }) {
  const [copyState, setCopyState] = useState("");
  const alive = useRef(false);
  const pending = useRef(false);
  const operation = useRef(0);
  const button = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    alive.current = true;
    return () => { alive.current = false; pending.current = false; operation.current += 1; };
  }, []);
  const copy = async () => {
    const node = button.current;
    if (!text || pending.current || !alive.current || !node?.isConnected) return;
    pending.current = true;
    const token = ++operation.current;
    setCopyState("正在复制…");
    const settle = (message: string) => {
      if (!alive.current || operation.current !== token || button.current !== node || !node.isConnected) return;
      pending.current = false;
      setCopyState(message);
    };
    try { await navigator.clipboard.writeText(text); settle("已复制核实资料"); }
    catch { settle("浏览器未允许复制，请手动选取下方文字"); }
  };
  const busy = copyState === "正在复制…";
  return <section className="ps-guide-copy"><div><strong>给家人照着走的文字版</strong><button ref={button} type="button" disabled={busy} aria-busy={busy} onClick={() => void copy()}>复制核实资料</button></div>{copyState ? <p role="status" aria-live="polite">{copyState}</p> : null}<textarea readOnly value={text} aria-label="攻略文字版" rows={6} /></section>;
}

function GuideContent({ guide }: { guide: TravelGuide }) {
  const { userId, household, pet } = useCurrentHousehold();
  const resourceKey = JSON.stringify([userId, household?.household_id, household?.home_id, pet?.pet_id, guide.guide_id, guide.journey_id, guide.image_url, guide.image_status]);
  const copyKey = JSON.stringify([userId, household?.household_id, household?.home_id, pet?.pet_id, guide.guide_id, guide.journey_id, guide.copy_text]);
  return <div className="ps-guide-content">
    <header className="ps-guide-hero"><span>TA 的旅行手账 · {guide.city}</span><h2 className="ps-guide-hero__title">{guide.title}</h2><p>{guide.summary || "这份手账暂时没有序言。"}</p><small>{STATUS_TEXT[guide.status ?? ""] ?? "状态待确认"}</small></header>
    {guide.image_url && guide.image_status === "ready" ? <GuideImage key={resourceKey} url={guide.image_url} /> : <div className="ps-guide-image-state"><Icon name="bookmark" size={28} /><span>{guide.image_status === "processing" ? "手账插画还在画" : guide.image_status === "failed" ? "这页插画没画成，文字照常可读" : guide.image_status === "unknown" ? "手账插画还没确认" : "这一页只有文字"}</span></div>}
    <div className="ps-guide-budget">{guide.coin_budget != null ? <span>星球内计划花费：{guide.coin_budget} 星币</span> : null}{guide.real_budget_note ? <span>现实费用：{guide.real_budget_note}</span> : null}</div>
    <h2>沿途停靠</h2>
    {guide.stops.length ? <ol className="ps-guide-stops">{guide.stops.map((stop, index) => <li key={`${stop.name}-${index}`}><span className="ps-guide-stops__number">{String(index + 1).padStart(2, "0")}</span><div><div className="ps-guide-stops__heading"><strong>{stop.label || stop.name}</strong><small>{stop.verified ? "地点已核实" : "地点未核实"}{guide.visited?.includes(stop.name) ? " · TA 已到过" : ""}</small></div>{stop.time ? <p>{stop.time}</p> : null}{stop.why ? <p>{stop.why}</p> : null}{stop.verified && stop.address ? <address>{stop.name} · {stop.address}</address> : null}{stop.tip ? <p className="ps-guide-stops__tip">{stop.tip}</p> : null}{stop.attribution ? <small>来源：{stop.attribution}</small> : null}{stop.verified && stop.nav_url?.startsWith("https://") ? <a href={stop.nav_url} target="_blank" rel="noopener noreferrer">打开地点导航 ↗</a> : null}</div></li>)}</ol> : <EmptyState icon="pin" title="还没有停靠点">TA 暂时没有写出可展示的站点。</EmptyState>}
    {guide.owner_tips.length ? <Card paper className="ps-guide-tips"><strong>家人留给 TA 的话</strong>{guide.owner_tips.map((tip, index) => <p key={`${index}-${tip}`}>{tip}</p>)}</Card> : null}
    {guide.copy_text ? <GuideCopy key={copyKey} text={guide.copy_text} /> : null}
  </div>;
}

function GuideUnavailable({ onRetry, listHref }: { onRetry: () => void; listHref: string }) {
  return <section className="ps-guide-unavailable" aria-label="手账查看选项">
    <EmptyState icon="lock" title="这份手账暂时无法查看" action={<div className="ps-guide-unavailable-actions">
      <Button icon="refresh" onClick={onRetry}>重新查看手账</Button>
      <SourceBack fallback={listHref} levelParent={listHref} className="ps-btn ps-btn--secondary"><Icon name="bookmark" size={17} />返回手账列表</SourceBack>
    </div>}>可以重新查看这份手账，或返回列表。</EmptyState>
  </section>;
}

export function GuideDetailPage() {
  const { guideId } = useParams();
  const location=useLocation();
  const entry=readSceneEntry(location.state,safeSceneHref(location.pathname+location.search));
  const listHref=entry?.parent.split("?",1)[0]==="/guides" ? entry.parent : "/guides";
  const { read: guides, authority, negativeOnly, retryFresh } = useGuides(guideId);
  const belongsToCurrentPet = guides.state === "ready" && guides.data.some(guide => guide.guide_id === guideId);
  const detail = useCurrentGuideDetail(authority, guides, guideId);
  return <Page className="ps-guide-page"><TopBar title="旅行手账" sourceBack={{ fallback: "/guides", levelParent: listHref }} />
    {negativeOnly && guides.state !== "error" ? <GuideUnavailable onRetry={retryFresh} listHref={listHref} /> : guides.state === "loading" || guides.state === "unavailable" ? <LoadingState label="正在确认 TA 的手账…" /> : guides.state === "error" ? <ErrorState error={guides.error} onRetry={guides.retry} /> : !belongsToCurrentPet ? <GuideUnavailable onRetry={retryFresh} listHref={listHref} /> : detail.state === "loading" || detail.state === "unavailable" ? <LoadingState label="正在翻开手账…" /> : detail.state === "error" ? <ErrorState error={detail.error} onRetry={detail.retry} /> : <GuideContent guide={detail.data} />}
  </Page>;
}
